// EP-135 Tier 1 — Committee member create modal. Admin+ only.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { Button, Input, Select } from '@/components/ui'
import { SearchableDropdown } from '@/components/ui/SearchableDropdown'
import { useCreateCommitteeMember } from '@/hooks/useGovernance'
import { useOwnerContracts } from '@/hooks/useOwnerContracts'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'

export interface CommitteeMemberFormModalProps {
  isOpen: boolean
  onClose: () => void
}

export function CommitteeMemberFormModal({ isOpen, onClose }: CommitteeMemberFormModalProps) {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')

  const [customerId, setCustomerId] = useState<number | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [role, setRole] = useState<'chair' | 'vice' | 'secretary' | 'treasurer' | 'member'>('chair')
  const [electedOn, setElectedOn] = useState('')
  const [termStartsOn, setTermStartsOn] = useState('')
  const [termEndsOn, setTermEndsOn] = useState('')

  const create = useCreateCommitteeMember()
  // Source the picker from the SHARED-OWNER roll, not the customer search.
  // Committee members must hold an OwnerContract (422
  // committee_member_not_a_shared_owner), and shared owners are deliberately
  // EXCLUDED from the Meilisearch customer index (Customer is configured
  // `unless: :shared_owner?`), so the old useCustomerSearch picker could
  // never return an eligible person. /api/shared_owners searches by SQL
  // ILIKE, so typing works; one person can hold several units, so dedupe
  // by customer id.
  const ownerContracts = useOwnerContracts(1, undefined, customerSearch || undefined)
  const eligible = (() => {
    const seen = new Map<number, string>()
    for (const lease of ownerContracts.data?.data ?? []) {
      const c = lease.customer
      if (c && !seen.has(c.id)) {
        seen.set(c.id, c.name || [c.first_name ?? '', c.last_name ?? ''].filter(Boolean).join(' ').trim())
      }
    }
    return [...seen.entries()].map(([id, label]) => ({ value: String(id), label }))
  })()

  const handleSave = async () => {
    if (!customerId) return
    try {
      await create.mutateAsync({
        customer_id: customerId,
        role,
        elected_on: electedOn || null,
        term_starts_on: termStartsOn || null,
        term_ends_on: termEndsOn || null,
      })
      setCustomerId(null)
      setRole('chair')
      setElectedOn('')
      setTermStartsOn('')
      setTermEndsOn('')
      onClose()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('committee.actions.new_member')}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!customerId}
            loading={create.isPending}
            data-testid="btn-save-member"
          >
            {tc('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label">
            <span className="label-text">{t('committee.labels.customer')}</span>
          </label>
          <SearchableDropdown
            value={customerId != null ? String(customerId) : ''}
            onChange={(v) => setCustomerId(v ? Number(v) : null)}
            onSearch={setCustomerSearch}
            options={eligible}
            placeholder={t('committee.labels.customer')}
          />
        </div>
        <Select
          label={t('committee.labels.role')}
          value={role}
          onChange={(e) => setRole(e.target.value as typeof role)}
          options={[
            { value: 'chair', label: t('committee.roles.chair') },
            { value: 'vice', label: t('committee.roles.vice') },
            { value: 'secretary', label: t('committee.roles.secretary') },
            { value: 'treasurer', label: t('committee.roles.treasurer') },
            { value: 'member', label: t('committee.roles.member') },
          ]}
        />
        <Input
          label={t('committee.labels.elected_on')}
          type="date"
          value={electedOn}
          onChange={(e) => setElectedOn(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t('committee.labels.term_starts_on')}
            type="date"
            value={termStartsOn}
            onChange={(e) => setTermStartsOn(e.target.value)}
          />
          <Input
            label={t('committee.labels.term_ends_on')}
            type="date"
            value={termEndsOn}
            onChange={(e) => setTermEndsOn(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  )
}
